import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollText, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { renderMarkdown } from "@/lib/markdown";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

/**
 * Phase L — Re-consent modal.
 *
 * Mounted globally inside the authenticated layouts. When the auth context
 * reports `terms_outdated` or `privacy_outdated` (after `/auth/login` flags
 * a version bump), this dialog opens and blocks dashboard interaction until
 * the user reviews the new text and posts `/auth/accept-terms`.
 *
 * The dialog is non-dismissable: closing it logs the user out so they can
 * never bypass the consent step.
 */
export function LegalConsentModal() {
  const { t, i18n } = useTranslation();
  const { legal, acknowledgeLegal, logout } = useAuth();

  const open = !!(legal?.terms_outdated || legal?.privacy_outdated);
  const lang = (i18n.language || "en").startsWith("es") ? "es" : "en";

  const [docs, setDocs] = useState({ terms: null, privacy: null });
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState("terms");

  // Default the first visible tab to whichever document is outdated.
  useEffect(() => {
    if (!open) return;
    if (legal.terms_outdated) setTab("terms");
    else if (legal.privacy_outdated) setTab("privacy");
  }, [open, legal.terms_outdated, legal.privacy_outdated]);

  // Fetch latest text on open. Both docs so the user can click between tabs
  // without round-tripping for every switch.
  useEffect(() => {
    if (!open) return;
    let aborted = false;
    setLoading(true);
    Promise.all([
      api.get(`/public/legal/terms`,   { params: { lang } }).then((r) => r.data).catch(() => null),
      api.get(`/public/legal/privacy`, { params: { lang } }).then((r) => r.data).catch(() => null),
    ]).then(([terms, privacy]) => {
      if (aborted) return;
      setDocs({ terms, privacy });
    }).finally(() => !aborted && setLoading(false));
    return () => { aborted = true; };
  }, [open, lang]);

  const renderedTerms = useMemo(
    () => renderMarkdown(docs.terms?.content || ""),
    [docs.terms],
  );
  const renderedPrivacy = useMemo(
    () => renderMarkdown(docs.privacy?.content || ""),
    [docs.privacy],
  );

  const onAccept = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/auth/accept-terms");
      acknowledgeLegal({
        terms_current:   r.data?.terms_current,
        privacy_current: r.data?.privacy_current,
      });
      toast.success(t("legal.acceptedToast", "Thanks — your acceptance was recorded."));
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
          || t("legal.acceptFailed", "Could not record your acceptance. Try again."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const onCancel = () => {
    // Closing the dialog without accepting = logout. Keeps the consent step
    // mandatory; user can always come back and accept later.
    logout();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v && !submitting) onCancel();
      }}
    >
      <DialogContent
        className="max-w-2xl"
        data-testid="legal-consent-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScrollText className="h-5 w-5 text-[hsl(var(--accent))]" />
            {t("legal.consentTitle", "Updated terms")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "legal.consentBody",
              "We've updated our Terms of Service and/or Privacy Policy. Please review the latest text and confirm to continue.",
            )}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="terms" data-testid="legal-tab-terms">
              {t("legal.termsTitle", "Terms of Service")}
              {docs.terms?.version ? (
                <span className="ml-2 text-[10px] text-muted-foreground font-mono">
                  v{docs.terms.version}
                </span>
              ) : null}
            </TabsTrigger>
            <TabsTrigger value="privacy" data-testid="legal-tab-privacy">
              {t("legal.privacyTitle", "Privacy Policy")}
              {docs.privacy?.version ? (
                <span className="ml-2 text-[10px] text-muted-foreground font-mono">
                  v{docs.privacy.version}
                </span>
              ) : null}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="terms" className="mt-3">
            <ScrollArea className="h-[40vh] rounded-md border border-border p-4 bg-background">
              {loading ? (
                <div className="space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-4 w-5/6" />
                </div>
              ) : (
                <article className="prose prose-sm max-w-none" data-testid="legal-modal-terms-body">
                  {renderedTerms}
                </article>
              )}
            </ScrollArea>
          </TabsContent>
          <TabsContent value="privacy" className="mt-3">
            <ScrollArea className="h-[40vh] rounded-md border border-border p-4 bg-background">
              {loading ? (
                <div className="space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-4 w-5/6" />
                </div>
              ) : (
                <article className="prose prose-sm max-w-none" data-testid="legal-modal-privacy-body">
                  {renderedPrivacy}
                </article>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>

        <label className="flex items-start gap-2 cursor-pointer text-xs text-muted-foreground mt-2">
          <Checkbox
            checked={accepted}
            onCheckedChange={(v) => setAccepted(!!v)}
            data-testid="legal-modal-checkbox"
            className="mt-0.5"
          />
          <span>
            {t(
              "legal.consentCheckbox",
              "I have read and agree to the latest Terms of Service and Privacy Policy.",
            )}
          </span>
        </label>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={submitting}
            data-testid="legal-modal-cancel"
          >
            {t("legal.declineSignOut", "Decline & sign out")}
          </Button>
          <Button
            type="button"
            onClick={onAccept}
            disabled={!accepted || submitting || loading}
            data-testid="legal-modal-accept"
          >
            <ShieldCheck className="h-4 w-4 mr-1" />
            {submitting
              ? t("legal.submitting", "Recording…")
              : t("legal.acceptContinue", "Accept and continue")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
